import type { SafeRect, StaticExportFormat } from '../types';
import { getSourceRect, type DynamicZoomViewport } from './dynamicZoom';

export interface FrameRect { x: number; y: number; width: number; height: number }

/** Safe area coordinates belong to the square master at referenceSize. */
export interface StaticFraming {
  format: StaticExportFormat;
  safeRect: SafeRect;
  referenceSize: number;
}

export function getStaticFormatAspect(format: StaticExportFormat): number {
  if (format === '16:9') return 16 / 9;
  if (format === '9:16') return 9 / 16;
  if (format === '2:3') return 2 / 3;
  return 1;
}

export function getFrameSize(longEdge: number, aspect: number): { width: number; height: number } {
  const size = Math.max(1, Math.round(longEdge));
  return aspect >= 1
    ? { width: size, height: Math.max(1, Math.round(size / aspect)) }
    : { width: Math.max(1, Math.round(size * aspect)), height: size };
}

export function getStaticFrameRect(width: number, height: number, framing: StaticFraming): FrameRect {
  if (framing.format === '1:1') return { x: 0, y: 0, width, height };
  if (framing.format === '1:1z') {
    const size = Math.min(width, height) / 1.5;
    return { x: (width - size) / 2, y: (height - size) / 2, width: size, height: size };
  }
  const sx = width / Math.max(1, framing.referenceSize);
  const sy = height / Math.max(1, framing.referenceSize);
  const safe = framing.safeRect;
  return computeSmartCrop(width, height, getStaticFormatAspect(framing.format), {
    x: safe.x * sx, y: safe.y * sy, w: safe.w * sx, h: safe.h * sy
  });
}

/** Keys remain normalized in the output frame, independently of its aspect. */
export function getFramedCameraRect(
  viewport: DynamicZoomViewport, width: number, height: number, framing: StaticFraming
): FrameRect {
  const frame = getStaticFrameRect(width, height, framing);
  const camera = getSourceRect(viewport, frame.width, frame.height);
  return { ...camera, x: frame.x + camera.x, y: frame.y + camera.y };
}

export function computeSmartCrop(
  sourceWidth: number, sourceHeight: number, targetAspect: number, safeRect: SafeRect
): FrameRect {
  const safeAspect = safeRect.w / Math.max(1, safeRect.h);
  let width: number;
  let height: number;
  if (targetAspect >= safeAspect) {
    height = safeRect.h; width = height * targetAspect;
  } else {
    width = safeRect.w; height = width / targetAspect;
  }
  if (width > sourceWidth) { width = sourceWidth; height = width / targetAspect; }
  if (height > sourceHeight) { height = sourceHeight; width = height * targetAspect; }
  return {
    x: clamp(safeRect.x + safeRect.w / 2 - width / 2, 0, sourceWidth - width),
    y: clamp(safeRect.y + safeRect.h / 2 - height / 2, 0, sourceHeight - height),
    width, height
  };
}

function clamp(value: number, min: number, max: number): number { return Math.max(min, Math.min(max, value)); }
